import { type PendingInvitation, TENANT_TYPE_NAMES } from "@closer/shared";
import { Badge, Button, Card, Spinner, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { CreateTenantModal } from "../../components/CreateTenantModal";
import {
  UsageArchetypeFields,
  useUsageArchetypeSelection,
} from "../../components/UsageArchetypeChooser";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";

/**
 * İlk giriş onboarding akışı (CLOS-224 `[047]`).
 *
 * Kullanıcı hesabını doğruladıktan sonra doğru ürün bağlamına yönlendirilir. Ekran üç
 * şeyi bir arada gösteriyor: hazır olan kişisel çalışma alanı, bekleyen davetler ve
 * organizasyon açma seçenekleri.
 *
 * ### Neden ayrı bir ekran
 *
 * `/tenants` seçim ekranı (`[042]`) "hangi alana gireyim" sorusunu cevaplıyor; onboarding
 * ise "burada ne var ve ne yapabilirim" sorusunu. İkisini birleştirmek, her girişte
 * onboarding metinlerini gören bir seçim ekranı üretirdi.
 *
 * ### Atlama meşru bir sonuç
 *
 * Kart atlamayı ayrı bir olay olarak istiyor. Davet kolu ve organizasyon açma kolu
 * *"hard blocker değildir"*: daveti olmayan ya da organizasyon açmayacak kullanıcı da
 * akışı bitirebilmeli.
 *
 * ### UI Tur 2 §7.17 — ACIK: SplitAuthLayout + Stepper replatformu YAPILMADI
 *
 * Spec bu ekranı "SplitAuthLayout'un kartı, Stepper" olarak tarif ediyor. AuthLayout'un
 * SplitAuthLayout'a bağlanması koordinatörden AÇIK yetki almıştı (§7.15); bu sayfa için
 * eşdeğer bir onay verilmedi. Ayrıca akış gerçek bir çok-adımlı sihirbaz DEĞİL: bölümler
 * (davetler/kullanım biçimi/organizasyon) veri varlığına göre koşullu görünüyor, sıralı
 * "1/2/3" adımları yok — `Stepper` eklemek NewContractCenter'da bilinçle kaçınılan "sahte
 * adım" hatasını burada tekrarlardı. Kabul kriterlerine dokunmadan sadece kart/buton/rozet
 * seviyesinde kalındı; kabuk düzeyi replatform kapsam dışı bırakıldı.
 */
export function OnboardingPage() {
  const { user, memberships, membershipsLoading, refreshSession } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  const davetlerQuery = useQuery({
    queryKey: ["invitations", "mine"],
    enabled: user !== null,
    queryFn: async () => (await api<PendingInvitation[]>("/api/invitations/mine")).data,
  });

  const bitir = useMutation({
    mutationFn: async (action: "complete" | "skip") =>
      api<{ onboardingStatus: string }>("/api/profile/onboarding", {
        method: "POST",
        body: JSON.stringify({ action }),
      }),
    onSuccess: async () => {
      /**
       * Oturum yükü onboarding durumunu taşıyor; tazelemeden yönlendirirsek guard
       * kullanıcıyı hemen buraya geri atar (yönlendirme döngüsü).
       */
      await refreshSession();
      navigate("/tenants");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı");
    },
  });

  const kisisel = memberships.find((m) => m.type === "individual");
  const organizasyonlar = memberships.filter((m) => m.type !== "individual");
  /** Kişisel alan hesapla birlikte doğuyor; yine de savunmacı davranıyoruz (`[022]`). */
  const kisiselAlan = memberships.find((m) => m.type === "individual") ?? null;
  const davetler = davetlerQuery.data ?? [];

  /**
   * ⚠ KULLANIM BİÇİMİ SEÇİMİ SAYFANIN ELİNDE (CLOS-860). Seçim bileşenin içinde kilitliyken
   * kullanıcı radyoyu işaretleyip doğrudan "Başlayalım"a bastığında seçim ne kaydediliyor ne
   * de uyarı üretiyordu: sessiz kayıp. Durum burada durduğu için birincil düğme onu görüyor.
   */
  const arketip = useUsageArchetypeSelection(kisiselAlan);

  /**
   * Birincil düğme, bekleyen seçimi ÖNCE yazar; ancak ondan sonra onboarding'i kapatır.
   *
   * ⚠ Seçenekler arasından "Başlayalım bekleyen seçimi de kaydeder" seçildi: en az sürpriz
   * olan yol bu. Radyonun anında kaydetmesi `/tenants`'taki Kaydet/Vazgeç akışını (CLOS-808)
   * ortadan kaldırırdı; salt uyarı ise kullanıcıyı hiçbir şey öğretmeden geri çevirirdi.
   *
   * ⚠ ARKETİP HÂLÂ ÇALIŞMA ALANI ALANI (CLOS-807): burada kaydedilen şey hesabın mesleği
   * değil, kişisel alanın kullanım biçimi — yazan uç de o alanın ucu. Değişen tek şey
   * kaydın hangi tıklamayla tetiklendiği.
   *
   * ⚠ Kayıt düşerse akış İLERLEMEZ: ilerleseydi kayıp yine sessiz kalırdı, kullanıcı
   * ekrandan çıkmış ve seçimi uygulanmamış olurdu. Hata bildirimini seçim hook'u veriyor.
   */
  const basla = async () => {
    try {
      await arketip.kaydetBekleyeni();
    } catch {
      return;
    }
    bitir.mutate("complete");
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col justify-center gap-4 p-4">
      <Card title={`Hoş geldiniz${user?.name ? `, ${user.name}` : ""}`}>
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-fg">Çalışma alanınız hazır</h3>
            {membershipsLoading ? (
              <Spinner />
            ) : kisisel ? (
              <p className="text-sm text-fg-muted">
                <span className="font-medium text-fg">{kisisel.name}</span> — kişisel çalışma
                alanınız hesabınızla birlikte oluşturuldu ve yalnız size aittir.
              </p>
            ) : (
              <p className="text-sm text-fg-muted">
                Kişisel çalışma alanınız hazırlanıyor. Sayfayı yenileyebilirsiniz.
              </p>
            )}
            {organizasyonlar.length > 0 ? (
              <p className="text-sm text-fg-muted">
                Ayrıca {organizasyonlar.length} organizasyona erişiminiz var.
              </p>
            ) : null}
          </section>

          {/* Daveti olmayan kullanıcı için bu kol HARD BLOCKER değil: hiç gösterilmiyor. */}
          {davetler.length > 0 ? (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-fg">Bekleyen davetleriniz</h3>
              <ul className="flex flex-col gap-2">
                {davetler.map((davet) => (
                  <li
                    key={davet.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-border-default px-4 py-3"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-fg">
                        {davet.organizationName}
                      </span>
                      <span className="block text-xs text-fg-soft">
                        {TENANT_TYPE_NAMES[davet.organizationType]} · {davet.roleName}
                      </span>
                    </span>
                    <Badge tone="success">Davet</Badge>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-fg-soft">
                Daveti kabul etmek için e-postanızdaki bağlantıyı kullanın.
              </p>
            </section>
          ) : null}

          {/*
            ⚠ KULLANIM BİÇİMİ İLK GİRİŞTE SORULUYOR (CLOS-808). Kart *"ilk onboarding içinde
            kişisel kullanım biçimi açıkça sorulmalı"* diyor. Organizasyon bölümünden ÖNCE
            duruyor: kişisel alan zaten var, organizasyon ise isteğe bağlı bir sonraki adım.
          */}
          {kisiselAlan ? (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-fg">Kişisel alanınız</h3>
              <UsageArchetypeFields alan={kisiselAlan} secim={arketip} />
            </section>
          ) : null}

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-fg">Organizasyon oluşturun</h3>
            <p className="text-sm text-fg-muted">
              Ekibinizle birlikte çalışmak için şirket ya da hukuk bürosu organizasyonu
              açabilirsiniz.
            </p>
            <div>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                Yeni Organizasyon
              </Button>
            </div>
          </section>

          <div className="flex flex-wrap gap-2 border-t border-border-default pt-4">
            <Button onClick={() => void basla()} loading={bitir.isPending || arketip.kaydediliyor}>
              Başlayalım
            </Button>
            {/*
              Atlama meşru bir sonuç ve AYRI bir olay üretiyor. ⚠ Bekleyen seçimi BİLEREK
              kaydetmiyor: "şimdilik atla" adım hakkında karar vermemek demek, birincil
              düğmenin taahhüdünü taşımıyor (CLOS-860 yalnız birincil yolu kapsıyor).
            */}
            <Button
              variant="secondary"
              onClick={() => bitir.mutate("skip")}
              loading={bitir.isPending}
              disabled={arketip.kaydediliyor}
            >
              Şimdilik atla
            </Button>
          </div>
        </div>
      </Card>

      <CreateTenantModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          void queryClient.invalidateQueries({ queryKey: ["memberships"] });
        }}
      />
    </div>
  );
}
